<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";

import AppToast from "@/components/base/AppToast.vue";
import AlbumSection from "@/components/gallery/AlbumSection.vue";
import AppHeader from "@/components/layout/AppHeader.vue";
import EventHero from "@/components/layout/EventHero.vue";
import QuickShare from "@/components/layout/QuickShare.vue";
import MediaLightbox from "@/components/lightbox/MediaLightbox.vue";
import UploadSheet from "@/components/upload/UploadSheet.vue";
import VoiceSheet from "@/components/voice/VoiceSheet.vue";
import type { FileSource } from "@/types/album";

import { useAlbumStore } from "@/stores/album";
import { useGuestbookStore } from "@/stores/guestbook";

const uploadOpen = ref(false);
const voiceOpen = ref(false);
const lightboxIndex = ref<number | null>(null);
const capturedFiles = ref<File[]>([]);
const capturedSource = ref<FileSource>("library");
const highlightedId = ref<string | null>(null);

const HIGHLIGHT_DURATION_MS = 1800;
let highlightTimer: ReturnType<typeof setTimeout> | undefined;

const album = useAlbumStore();
const guestbook = useGuestbookStore();

/**
 * Photothèque, appareil photo et glisser-déposer débouchent au même endroit :
 * la feuille d'ajout, ouverte une fois les fichiers en main, directement sur
 * la revue où l'invité nomme son souvenir.
 */
const onIncomingFiles = (files: File[], source: FileSource): void => {
  capturedFiles.value = files;
  capturedSource.value = source;
  uploadOpen.value = true;
};

/**
 * Une carte sélectionnée dans la grille ouvre la visionneuse à son index
 * courant. L'index se calcule sur `filteredItems`, la liste réellement
 * affichée (et transmise à la visionneuse) : calculer sur `album.items`
 * désynchronise l'index dès qu'un filtre est actif.
 */
const onOpenMedia = (id: string): void => {
  const index = album.filteredItems.findIndex((item) => item.id === id);
  if (index >= 0) lightboxIndex.value = index;
};

/**
 * « Voir dans la galerie » doit amener l'invité jusqu'à sa carte, pas seulement
 * refermer la feuille : la mise en avant existait déjà mais se jouait hors de
 * l'écran, plusieurs hauteurs plus bas, donc invisible.
 *
 * `nextTick` attend que la carte soit rendue, et le défilement est instantané
 * plutôt qu'animé : sur une galerie longue, un défilement doux mettrait
 * plusieurs secondes et l'animation de mise en avant serait finie à l'arrivée.
 */
const onHighlight = async (id: string): Promise<void> => {
  if (highlightTimer !== undefined) clearTimeout(highlightTimer);
  highlightedId.value = id;

  await nextTick();
  const card = document.querySelector(`[data-media-id="${id}"]`);
  card?.scrollIntoView({ block: "center" });

  highlightTimer = setTimeout(() => {
    highlightedId.value = null;
    highlightTimer = undefined;
  }, HIGHLIGHT_DURATION_MS);
};

onMounted(() => {
  void album.hydrate();
  void guestbook.hydrate();
});

onBeforeUnmount(() => {
  if (highlightTimer !== undefined) clearTimeout(highlightTimer);
  album.disposeAll();
  guestbook.disposeAll();
});
</script>

<template>
  <div class="grain" aria-hidden="true"></div>

  <AppHeader />

  <!-- Le toast qui signale l'indisponibilité du stockage s'efface au bout de
       quelques secondes. Cette information-là doit rester visible : sans elle,
       l'invité dépose des souvenirs en croyant qu'ils seront encore là demain. -->
  <p v-if="album.isReady && !album.isPersistent" class="storage-warning" role="status">
    Cet appareil ne peut pas conserver vos souvenirs&nbsp;: ils resteront visibles le temps de cette
    visite seulement.
  </p>

  <main id="top">
    <EventHero />
    <QuickShare @open-voice="voiceOpen = true" @files="onIncomingFiles" />
    <AlbumSection :highlighted-id="highlightedId" @open-media="onOpenMedia" />
  </main>

  <UploadSheet
    v-model:open="uploadOpen"
    :initial-files="capturedFiles"
    :source="capturedSource"
    @highlight="(id) => void onHighlight(id)"
  />
  <VoiceSheet v-model:open="voiceOpen" />
  <MediaLightbox v-model:index="lightboxIndex" :items="album.filteredItems" />

  <AppToast />
</template>

<style scoped>
.grain {
  position: fixed;
  inset: 0;
  z-index: 100;
  opacity: 0.035;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 160 160' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}

main {
  width: min(100%, 1320px);
  margin: auto;
}

.storage-warning {
  margin: 0;
  padding: 9px 16px;
  color: var(--color-ink);
  border-bottom: 1px solid var(--color-line);
  background: rgb(213 177 68 / 0.18);
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
}
</style>
